/* Reading progress + history, local-only trackers.
 *
 * The app has no auth, so everything is persisted in localStorage on the device.
 * - cm_reading_progress_v1 → single most-recent entry (powers the "Continuar leitura" card on Home).
 * - cm_reading_history_v1  → ordered list of up to 20 unique entries (powers the "Histórico" screen).
 *
 * Both stores share the same payload schema so a history item can be navigated
 * the same way as the "continue" card.
 */

const PROGRESS_KEY = "cm_reading_progress_v1";
const HISTORY_KEY = "cm_reading_history_v1";
const PLAN_PROGRESS_KEY = "cm_plan_progress_v1";
const HISTORY_LIMIT = 20;

// ---------- Safe storage helpers ----------
const safeRead = (key) => {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota, ignore */
  }
};

// ---------- Progress (single entry, used by Home "Continuar leitura") ----------
export const saveReadingProgress = (entry) => {
  if (!entry || !entry.type) return;
  safeWrite(PROGRESS_KEY, { ...entry, updated_at: new Date().toISOString() });
};

export const getReadingProgress = () => safeRead(PROGRESS_KEY);

export const clearReadingProgress = () => {
  try {
    window.localStorage.removeItem(PROGRESS_KEY);
  } catch {
    /* ignore */
  }
};

// ---------- History (ordered list, max 20, dedup by href) ----------
const sanitizeHistory = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (it) => it && typeof it === "object" && typeof it.href === "string" && it.href
  );
};

export const getReadingHistory = () => sanitizeHistory(safeRead(HISTORY_KEY));

export const clearReadingHistory = () => {
  try {
    window.localStorage.removeItem(HISTORY_KEY);
  } catch {
    /* ignore */
  }
};

const buildHistoryEntry = (entry) => ({
  id: entry.id || `${entry.type}-${Date.now()}`,
  type: entry.type,
  title: entry.title || "",
  subtitle: entry.subtitle || "",
  href: entry.href,
  updated_at: new Date().toISOString(),
  // Optional metadata, kept only when present so nothing leaks across types.
  ...(entry.plan_slug ? { plan_slug: entry.plan_slug } : {}),
  ...(entry.plan_title ? { plan_title: entry.plan_title } : {}),
  ...(entry.day_number ? { day_number: entry.day_number } : {}),
  ...(entry.sermon_slug ? { sermon_slug: entry.sermon_slug } : {}),
  ...(entry.devotional_id ? { devotional_id: entry.devotional_id } : {}),
  ...(entry.book_slug ? { book_slug: entry.book_slug } : {}),
  ...(entry.book_name ? { book_name: entry.book_name } : {}),
  ...(entry.chapter ? { chapter: entry.chapter } : {}),
  ...(entry.version ? { version: entry.version } : {}),
});

export const pushHistory = (entry) => {
  if (!entry || !entry.type || !entry.href) return;
  const normalized = buildHistoryEntry(entry);
  const current = getReadingHistory().filter((it) => it.href !== normalized.href);
  const next = [normalized, ...current].slice(0, HISTORY_LIMIT);
  safeWrite(HISTORY_KEY, next);
};

// ---------- Subtitle formatting ----------
export const formatProgressSubtitle = (entry) => {
  if (!entry) return "";
  if (entry.type === "devotional") {
    return entry.title ? `Você parou em "${entry.title}"` : "Você parou no devocional";
  }
  if (entry.type === "sermon") {
    return entry.title ? `Você parou no sermão "${entry.title}"` : "Você parou no sermão";
  }
  if (entry.type === "plan_day") {
    const planLabel = entry.plan_title ? ` de ${entry.plan_title}` : "";
    const day = entry.day_number || entry.day;
    return `Você parou no Dia ${day}${planLabel}`;
  }
  if (entry.type === "bible_chapter") {
    return entry.title ? `Você parou em ${entry.title}` : "Você parou na Bíblia";
  }
  return "";
};

export const labelForType = (type) => {
  if (type === "devotional") return "Devocional";
  if (type === "sermon") return "Sermão";
  if (type === "plan_day") return "Plano";
  if (type === "bible_chapter") return "Bíblia";
  return "Leitura";
};

// ---------- Tracking helpers (persist progress + push to history) ----------
const trackBoth = (entry) => {
  saveReadingProgress(entry);
  pushHistory({ ...entry, subtitle: formatProgressSubtitle(entry) });
};

export const trackDevotional = ({ id, title }) => {
  if (!title) return;
  trackBoth({
    type: "devotional",
    id: id || "today",
    title,
    href: id ? `/devocional/${id}` : "/devocional",
    devotional_id: id || "",
  });
};

export const trackSermon = ({ id, title }) => {
  if (!id || !title) return;
  trackBoth({
    type: "sermon",
    id,
    title,
    href: `/sermoes/${id}`,
    sermon_slug: id,
  });
};

export const trackPlanDay = ({ slug, planTitle, day, dayTitle, totalDays }) => {
  if (!slug || !day) return;
  trackBoth({
    type: "plan_day",
    slug,
    plan_title: planTitle || "",
    plan_slug: slug,
    day,
    day_number: day,
    day_title: dayTitle || "",
    total_days: totalDays || 0,
    title: dayTitle || `Dia ${day}`,
    href: `/planos/${slug}/dia/${day}`,
  });
  recordPlanDayRead({ slug, planTitle, day, totalDays });
};

export const trackBibleChapter = ({ book_slug, book_name, chapter, version }) => {
  if (!book_slug || !chapter) return;
  const display = `${book_name || book_slug} ${chapter}`;
  trackBoth({
    type: "bible_chapter",
    id: `${book_slug}-${chapter}`,
    title: display,
    href: `/biblia/${book_slug}/${chapter}`,
    book_slug,
    book_name: book_name || "",
    chapter,
    version: version || "",
  });
};

// ---------- Plan progress (per slug, persistent journey) ----------
const sanitizePlanMap = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  Object.entries(raw).forEach(([slug, value]) => {
    if (!value || typeof value !== "object" || !slug) return;
    const completed = Array.isArray(value.completed_days)
      ? Array.from(new Set(value.completed_days.filter((n) => Number.isInteger(n) && n > 0))).sort((a, b) => a - b)
      : [];
    out[slug] = {
      plan_slug: value.plan_slug || slug,
      plan_title: typeof value.plan_title === "string" ? value.plan_title : "",
      total_days: Number.isInteger(value.total_days) ? value.total_days : 0,
      completed_days: completed,
      current_day: Number.isInteger(value.current_day) ? value.current_day : (completed[completed.length - 1] || 1),
      last_day_read: Number.isInteger(value.last_day_read) ? value.last_day_read : null,
      last_href: typeof value.last_href === "string" ? value.last_href : "",
      started_at: typeof value.started_at === "string" ? value.started_at : null,
      updated_at: typeof value.updated_at === "string" ? value.updated_at : null,
      completed_at: typeof value.completed_at === "string" ? value.completed_at : null,
    };
  });
  return out;
};

export const getAllPlanProgress = () => sanitizePlanMap(safeRead(PLAN_PROGRESS_KEY));

export const getPlanProgress = (slug) => {
  if (!slug) return null;
  const all = getAllPlanProgress();
  return all[slug] || null;
};

export const clearPlanProgress = (slug) => {
  try {
    if (!slug) {
      window.localStorage.removeItem(PLAN_PROGRESS_KEY);
      return;
    }
    const all = getAllPlanProgress();
    delete all[slug];
    safeWrite(PLAN_PROGRESS_KEY, all);
  } catch {
    /* ignore */
  }
};

const computeNextDay = (completed, total) => {
  if (!total || total < 1) return null;
  for (let d = 1; d <= total; d += 1) {
    if (!completed.includes(d)) return d;
  }
  return null; // all completed
};

export const recordPlanDayRead = ({ slug, planTitle, day, totalDays }) => {
  if (!slug || !day) return;
  const now = new Date().toISOString();
  const all = getAllPlanProgress();
  const prev = all[slug];
  const completed = Array.from(new Set([...(prev?.completed_days || []), day])).sort((a, b) => a - b);
  const total = totalDays || prev?.total_days || 0;
  const next = computeNextDay(completed, total);
  const allDone = total > 0 && next === null;
  all[slug] = {
    plan_slug: slug,
    plan_title: planTitle || prev?.plan_title || "",
    total_days: total,
    completed_days: completed,
    current_day: allDone ? total : (next || day),
    last_day_read: day,
    last_href: `/planos/${slug}/dia/${day}`,
    started_at: prev?.started_at || now,
    updated_at: now,
    completed_at: allDone ? (prev?.completed_at || now) : null,
  };
  safeWrite(PLAN_PROGRESS_KEY, all);
};

/** Build a lightweight summary used by Plan list cards. */
export const summarizePlanProgress = (slug, totalDaysFallback = 0) => {
  const p = getPlanProgress(slug);
  const total = (p?.total_days && p.total_days > 0) ? p.total_days : totalDaysFallback;
  const done = p?.completed_days?.length || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isCompleted = !!p?.completed_at || (total > 0 && done >= total);
  const currentDay = p?.current_day || (done > 0 ? Math.min(done + 1, total || done + 1) : 1);
  return {
    started: !!p,
    completed: isCompleted,
    completed_days_count: done,
    total_days: total,
    percent: pct,
    current_day: currentDay,
    last_href: p?.last_href || (slug ? `/planos/${slug}/dia/${currentDay}` : ""),
    plan_title: p?.plan_title || "",
    updated_at: p?.updated_at || null,
  };
};

/** Returns the most recently updated unfinished plan, or null. */
export const getActivePlan = () => {
  const all = getAllPlanProgress();
  const candidates = Object.values(all).filter((p) => !p.completed_at);
  if (!candidates.length) return null;
  candidates.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
  return candidates[0];
};
